import { gql, useMutation } from '@apollo/client';
import { AncestorIdData, AncestorNameData } from 'modules/domains/foldering';
import {
  addNegativeToast,
  addNegativeToastWithErrorId,
  addPositiveToast,
} from 'modules/toastContainer';
import { useDispatch } from 'react-redux';
import { FolderNameLink } from 'ui/indexPages/folders/Folders.styles';
import { NamedFolder, ResponseWrapper } from 'ui/toast';
import { FOLDER_ID_SEARCH_PARAM, PAGE_SEARCH_PARAM } from '../constants';
import { useCallback } from 'react';

export type CreateFolderResponseData = {
  createCampaignFolder: number;
};

export type Variables = {
  name: string;
  locationId?: number | null;
};

export const CreateCampaignFolderQuery = gql`
  mutation CreateCampaignFolder($name: String!, $locationId: Long) {
    createCampaignFolder(name: $name, locationId: $locationId)
  }
`;

const displayText = {
  genericError: 'Could not create folder',
};

export const useCreateCampaignFolder = () => {
  const dispatch = useDispatch();

  const [createFolder] = useMutation<CreateFolderResponseData, Variables>(
    CreateCampaignFolderQuery,
  );

  return useCallback(
    async (newFolderName: string, newFolderDestination: AncestorIdData & AncestorNameData) => {
      try {
        const { data: creationMutationData, errors: creationMutationErrors } = await createFolder({
          variables: {
            name: newFolderName,
            locationId: newFolderDestination.id,
          },
        });

        const destinationFolderName =
          newFolderDestination.name === '__root__' ? 'Main' : newFolderDestination.name;

        if (!creationMutationData?.createCampaignFolder || creationMutationErrors) {
          dispatch(
            addNegativeToast(
              <ResponseWrapper key="toast-error">
                <NamedFolder name={newFolderName} /> could not be created
                {destinationFolderName ? (
                  <span>
                    {' '}
                    in the <NamedFolder name={destinationFolderName} /> folder.
                  </span>
                ) : (
                  '.'
                )}
              </ResponseWrapper>,
            ),
          );
          return;
        }

        const newFolderId = creationMutationData.createCampaignFolder;
        const { location } = window;
        const urlParams = new URLSearchParams(location.search);
        urlParams.set(FOLDER_ID_SEARCH_PARAM, newFolderId.toString());
        urlParams.delete(PAGE_SEARCH_PARAM);

        dispatch(
          addPositiveToast(
            <ResponseWrapper>
              <FolderNameLink to={`${location.pathname}?${urlParams.toString()}`}>
                <NamedFolder name={newFolderName} />
              </FolderNameLink>{' '}
              was successfully created
              {destinationFolderName ? (
                <span>
                  {' '}
                  in the <NamedFolder name={destinationFolderName} /> folder.
                </span>
              ) : (
                '.'
              )}
            </ResponseWrapper>,
          ),
        );
      } catch (e) {
        dispatch(
          addNegativeToastWithErrorId(displayText.genericError, 'useCreateCampaignFolder', e),
        );
      }
    },
    [createFolder, dispatch],
  );
};
